/**
Copyright (c) 2022–2026 Podstack.ai
All rights reserved.
Proprietary and Confidential.
Unauthorized copying of this file, via any medium, is strictly prohibited.
This file is part of the Podstack Platform.
*/

import React, { useState } from 'react';
import { FolderPlusIcon } from '@heroicons/react/24/outline';
import { useProjects } from '../../hooks/useProjects';
import { useSelectedProject } from '../../hooks/useSelectedProject';

interface ProjectOnboardingModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const ProjectOnboardingModal: React.FC<ProjectOnboardingModalProps> = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState({ name: '', description: '' });
  const { createProject, isCreating } = useProjects();
  const { setSelectedProject } = useSelectedProject();

  if (!isOpen) return null;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name.trim()) return;

    createProject(
      { name: formData.name.trim(), description: formData.description.trim() },
      {
        onSuccess: (response: any) => {
          const newProject = response.data?.data || response.data;
          if (newProject?.id) {
            setSelectedProject(newProject.id);
          }
          setFormData({ name: '', description: '' });
          onClose();
        },
      }
    );
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50">
      <div className="bg-gray-900 border border-gray-800 rounded-2xl shadow-xl w-full max-w-md mx-4">
        <div className="p-6 text-center border-b border-gray-700">
          <div className="w-12 h-12 bg-primary-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <FolderPlusIcon className="w-6 h-6 text-primary-400" />
          </div>
          <h3 className="text-lg font-semibold text-white">
            Welcome! Let's get started
          </h3>
          <p className="text-sm text-gray-400 mt-2">
            Create your first project to organize your resources.
          </p>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">
              Project Name
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="input"
              placeholder="My ML Project"
              required
              autoFocus
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">
              Description
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className="input resize-none"
              rows={3}
              placeholder="Optional description..."
            />
          </div>
          <button
            type="submit"
            className="btn-primary w-full"
            disabled={isCreating || !formData.name.trim()}
          >
            {isCreating ? 'Creating...' : 'Create Project'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default ProjectOnboardingModal;
